/* =============================================================================
   CORE / EMAIL-TEMPLATES — Vorlagen-Engine + Mail-Versand-Wrapper
   =============================================================================
   Drei verwandte Aufgaben:
     - Platzhalter in Vorlagen-Strings ersetzen (`fillTemplate`)
     - Cancel-Link bauen (Frontend-URL mit #cancel=<token>)
     - Mailversand-Wrapper für Bestätigung / Absage / Erinnerung
       (delegieren an App.mailer, der je nach Provider mailto oder
       Resend via Edge Function aufruft)
   - Toast-Hint je nach Provider (`mailResultToast`)

   `area-Label`-Lookup und `tableNamesFor` (für die {area}-Spalte) leben
   weiterhin in app.js — wir referenzieren sie über `window.*` für die
   Übergangsphase. Beim Ausziehen der Render-Module kommen sie in passende
   Geschwister-Module.
============================================================================ */

import {
  fmtDate, fmtEUR, showToast,
  normalizePreorderItems, preorderSpecialRequests,
  // S86: Kassenbon-Items aus order_items (DB-Single-Source) wenn Snapshot leer
  // (Cockpit-Orders). Wiederverwendung der S81-A-Brücke.
  snapshotHasRenderableItems, itemsToPreorderSnapshot,
  displayGuestName
} from '../utils/helpers.js';
import { inlineBrandLogoForEmail, wrapBodyWithBrandHtml, wrapHtmlWithBrandHtml } from './branding.js';
import { receiptHtmlToPdfBase64 } from '../utils/receipt-pdf.js';
// S225 CUT B — Kind-Toggle-Spiegel für die Direct-Send-Belege (dispatch_source
// 'queue'/'direct' via RPC laufen am DB-Trigger-Gate vorbei → JS-seitig prüfen).
// mail-kinds.js ist bewusst import-frei (kein Zyklus über mail-template-defaults).
import { isMailKindEnabled } from './mail-kinds.js';

// -----------------------------------------------------------------------------
// S45 Block C — Single-Source-Template-Defaults
// -----------------------------------------------------------------------------
// Alle Mail-Wortlaute für JS-Pfad UND Edge-Function-Pfad sitzen ab S45 hier
// (JS) bzw. in supabase/functions/process-mail-outbox/index.ts KIND_TEMPLATES
// (Edge-Function). Beide Files müssen in Sync gehalten werden — Test in
// email-templates.test.js prüft die Default-Subjects gegen die Edge-Defaults
// (siehe describe('Default-Templates — Single-Source-Sync')).
//
// Lookup-Reihenfolge im JS-Resolver (resolveReservationTemplate/-Order):
//   1) settings.emailTemplates[kind]      (Sub-Object {subject, body}, neu)
//   2) settings.<legacyField>             (flach, Backward-Compat aus pre-S45)
//   3) DEFAULT_xxx_TEMPLATES[kind]        (Code-Default, immer gefüllt)
//
// settings.emailTemplatesOrder.<kind> ist seit Migration 31 ein Sub-Object
// — dort kein Legacy-Flat-Pfad nötig, nur Settings-Sub-Object || Default.
// -----------------------------------------------------------------------------

/** Reservation-Templates nutzen Single-Curly-Tokens ({name}, {date}, …).
 *  S94: `received` (Eingangsbestätigung) entfernt — die erste Mail geht
 *  grundsätzlich erst nach interner Bestätigung raus (Jonas-Direktive). */
export const DEFAULT_RESERVATION_TEMPLATES = Object.freeze({
  confirmed: Object.freeze({
    subject: 'Reservierungsbestätigung — {date}, {time} Uhr',
    body:
`Hallo {name},

deine Reservierung ist bestätigt:

  📅  {date}
  ⏰  {time} Uhr
  👥  {guests} Personen
  📍  {area}

Falls sich deine Pläne ändern, kannst du hier stornieren:
{cancel_link}

Wir freuen uns auf deinen Besuch!
Dein {restaurant}-Team
{phone}`
  }),
  declined: Object.freeze({
    subject: 'Zu deiner Reservierungsanfrage am {date}',
    body:
`Hallo {name},

leider können wir deine Reservierungsanfrage für den {date} um {time} Uhr
nicht bestätigen. Vielleicht klappt es bei einem anderen Termin?

Schreib uns gern direkt zurück oder ruf an unter {phone}.

Viele Grüße
Dein {restaurant}-Team`
  }),
  reminder: Object.freeze({
    subject: 'Erinnerung: deine Reservierung morgen — {date}, {time} Uhr',
    body:
`Hallo {name},

kurze Erinnerung: wir freuen uns auf dich morgen!

  📅  {date}
  ⏰  {time} Uhr
  👥  {guests} Personen

Falls sich deine Pläne geändert haben, kannst du hier stornieren:
{cancel_link}

Bis bald!
Dein {restaurant}-Team
{phone}`
  }),
  // S45 Hotfix2 / Phase 36 — Reservation-Storno-Bestätigung. Im Cron-Pfad
  // primär verwendet (Migration 36 Trigger); JS-Default als Fallback für
  // potentielle Manuell-Sender in Admin-UI.
  cancelled: Object.freeze({
    subject: 'Reservierung am {date} storniert',
    body:
`Hallo {name},

deine Reservierung für {guests} Personen am {date} um {time} Uhr wurde storniert. ❌

Falls das nicht beabsichtigt war oder du es dir anders überlegt hast, freuen wir uns auf eine neue Anfrage.

Dein {restaurant}-Team
{phone}`
  })
});

/** Order-Templates nutzen Double-Curly-Tokens ({{name}}, {{order_number}}, …).
 *  S94: `received` (Eingangsbestätigung) entfernt — die erste Mail geht
 *  grundsätzlich erst nach interner Bestätigung raus (Jonas-Direktive). */
export const DEFAULT_ORDER_TEMPLATES = Object.freeze({
  confirmed: Object.freeze({
    subject: 'Bestellung {{order_number}} bestätigt',
    body:
`Hallo {{name}},

wir haben deine Bestellung bestätigt — alles in der Pipeline.

  🛒  Bestellung {{order_number}}
  ⏰  Abholung um {{pickup_time}} Uhr

Falls du die Bestellung stornieren möchtest:
{{cancel_link}}

Bis gleich!
dein {{brand}}-Team`
  }),
  ready: Object.freeze({
    subject: 'Deine Bestellung {{order_number}} ist abholbereit! 🥡',
    body:
`Hallo {{name}},

deine Bestellung wartet bei uns — komm vorbei wann es dir passt!

  🥡  Bestellung {{order_number}}
  📍  Abholung jetzt möglich

Bis gleich,
dein {{brand}}-Team`
  }),
  cancelled: Object.freeze({
    subject: 'Bestellung {{order_number}} storniert',
    body:
`Hallo {{name}},

deine Bestellung wurde storniert. ❌ Falls das ein Versehen war, melde dich gerne bei uns.

Viele Grüße,
dein {{brand}}-Team
{{phone}}`
  }),
  receipt: Object.freeze({
    subject: 'Dein Kassenbon zur Bestellung {{order_number}}',
    body:
`Hallo {{name}},

vielen Dank für deinen Einkauf bei {{brand}}!
Anbei dein Kassenbon zur Bestellung {{order_number}} (Rg. {{invoice_number}}, {{payment_method}}).
Gesamtbetrag: {{total}}.

{{items}}

Viele Grüße
dein {{brand}}-Team`
  }),
  // S94 — Stornobeleg (Korrekturbeleg). Bis S93 inline in sendOrderReceiptEmail
  // hartcodiert; jetzt editierbar wie der Kassenbon (Override-Slot
  // emailTemplatesOrder.correction → resolveOrderTemplate('correction')).
  correction: Object.freeze({
    subject: 'Stornobeleg zur Bestellung {{order_number}}',
    body:
`Hallo {{name}},

der Betrag deiner Bestellung {{order_number}} wurde erstattet. Beleg-Nr {{invoice_number}}.

{{items}}

Viele Grüße
dein {{brand}}-Team`
  })
});

/** Combined-kind reservation_with_order_confirmed (S44). */
export const DEFAULT_RESERVATION_COMBINED_TEMPLATE = Object.freeze({
  subject: 'Reservierung & Bestellung am {{reservation_date}} bestätigt',
  body:
`Hallo {{name}},

deine Reservierung für {{guests}} Personen am {{reservation_date}} um {{reservation_time}} Uhr ist bestätigt — inklusive deiner Vorbestellung.

{{items_block}}

Gesamtsumme: {{total}}

Falls sich deine Pläne ändern, kannst du hier stornieren:
{{cancel_link}}

Wir freuen uns auf deinen Besuch!

Dein {{brand}}-Team`
});

/** S85 — Export-Mails (an Steuerberater/Buchhaltung). Tokens: {{zbon_number}}
 *  {{date}} {{gross}} {{count}} {{brand}}. Der Z-Bon-HTML-Beleg selbst wird
 *  generiert (payload.html); dieses Template ist der editierbare Cover-Text. */
export const DEFAULT_EXPORT_TEMPLATES = Object.freeze({
  zbon: Object.freeze({
    subject: 'Z-Bon {{zbon_number}} · {{date}} · {{brand}}',
    body:
`Tagesabschluss-Beleg vom {{date}} (Z-Bon {{zbon_number}}).

Brutto: {{gross}}
Anzahl Bestellungen: {{count}}

— gesendet aus {{brand}} Cockpit`
  })
});

/** S85 — Resolver für Export-Templates: Tenant-Override
 *  (settings.emailTemplatesExport[kind]) → DEFAULT_EXPORT_TEMPLATES. */
export function resolveExportTemplate(state, kind) {
  const def = DEFAULT_EXPORT_TEMPLATES[kind];
  if (!def) return null;
  const settings = (state && state.settings) || {};
  const ov = settings.emailTemplatesExport && settings.emailTemplatesExport[kind];
  if (ov && typeof ov === 'object') {
    const subject = (typeof ov.subject === 'string' && ov.subject.trim()) ? ov.subject : def.subject;
    const body    = (typeof ov.body    === 'string' && ov.body.trim())    ? ov.body    : def.body;
    return { subject, body };
  }
  return { subject: def.subject, body: def.body };
}

/** S85 — Token-Substitution für Export-Mails (double-brace). */
export function fillExportTemplate(tpl, vars) {
  if (typeof tpl !== 'string') return '';
  let out = tpl;
  for (const [k, v] of Object.entries(vars || {})) {
    out = out.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), v == null ? '' : String(v));
  }
  return out;
}

/** Legacy-Flat-Field-Map: pre-S45 Settings-Pfade pro Reservation-kind.
 *  Lookup-Order im Resolver: emailTemplates[kind] → legacy → DEFAULT.
 *  Tenants, die ihre alten Wortlaute personalisiert haben, behalten sie. */
export const LEGACY_RESERVATION_BODY_FIELDS = Object.freeze({
  confirmed: 'confirmEmailTemplate',
  declined:  'declineEmailTemplate',
  reminder:  'reminderEmailTemplate'
});

/** Resolver für Reservation-Templates. Liefert IMMER ein {subject, body}-Objekt.
 *
 *  Lookup-Reihenfolge:
 *    1) settings.emailTemplates[kind] (Sub-Object, neu seit S45)
 *    2) settings.<legacyField>        (flache pre-S45-Felder, nur Body —
 *                                      Subject kommt aus DEFAULT)
 *    3) DEFAULT_RESERVATION_TEMPLATES[kind]
 *
 *  Liefert NULL bei unbekanntem kind (Fail-Loud — Caller entscheidet, ob
 *  Fallback oder Error).
 */
export function resolveReservationTemplate(state, kind) {
  const def = DEFAULT_RESERVATION_TEMPLATES[kind];
  if (!def) return null;
  const settings = (state && state.settings) || {};

  // 1) Sub-Object Override (neuer Pfad)
  const subObj = settings.emailTemplates && settings.emailTemplates[kind];
  if (subObj && typeof subObj === 'object') {
    const subject = (typeof subObj.subject === 'string' && subObj.subject.trim()) ? subObj.subject : def.subject;
    const body    = (typeof subObj.body    === 'string' && subObj.body.trim())    ? subObj.body    : def.body;
    return { subject, body };
  }

  // 2) Legacy-Flat-Field (Tenant-Wortlaut aus pre-S45)
  const legacyField = LEGACY_RESERVATION_BODY_FIELDS[kind];
  if (legacyField && typeof settings[legacyField] === 'string' && settings[legacyField].trim()) {
    return { subject: def.subject, body: settings[legacyField] };
  }

  // 3) Default
  return { subject: def.subject, body: def.body };
}

/** Resolver für Order-Templates. Settings-Sub-Object || DEFAULT.
 *  Anders als Reservation-Resolver: kein Legacy-Flat-Pfad — Order-Templates
 *  liegen seit Migration 31 immer schon als Sub-Object in
 *  settings.emailTemplatesOrder[kind]. */
export function resolveOrderTemplate(state, kind) {
  const def = DEFAULT_ORDER_TEMPLATES[kind];
  if (!def) return null;
  const settings = (state && state.settings) || {};
  const subObj   = settings.emailTemplatesOrder && settings.emailTemplatesOrder[kind];
  if (subObj && typeof subObj === 'object') {
    const subject = (typeof subObj.subject === 'string' && subObj.subject.trim()) ? subObj.subject : def.subject;
    const body    = (typeof subObj.body    === 'string' && subObj.body.trim())    ? subObj.body    : def.body;
    return { subject, body };
  }
  return { subject: def.subject, body: def.body };
}



/** Phase 31 (S29): Helper, der ein Text-Body in Brand-HTML wrappt und ans
 *  Mailer-Payload anhängt. Liefert dasselbe Result wie App.mailer.send,
 *  inklusive Fallback wenn das HTML-Wrapping fehlschlägt (dann Text-only). */
async function _mailWithBrandHtml(payload) {
  const state = getState();
  const restaurantName = (state.settings && state.settings.restaurantName) || 'Grill & Green';
  let html;
  try {
    html = await wrapBodyWithBrandHtml(payload.body, { restaurantName });
  } catch (e) {
    console.warn('[mailer] HTML-Wrap fehlgeschlagen, sende Text-only:', e);
  }
  return window.App.mailer.send({ ...payload, html });
}

/**
 * S93-C — baut aus einem Standalone-Beleg-HTML einen PDF-Mailanhang
 * (`[{ filename, content:<base64> }]`). Best-effort: bei Fehler `null`, damit der
 * Versand ohne Anhang (HTML-Fallback) weiterläuft. Logo wird vor dem Capture als
 * Data-URL eingebettet (html2canvas-tauglich). Quelle = bestehendes Beleg-HTML
 * (Single-Source, kein Layout-Duplikat).
 */
export async function _buildReceiptPdfAttachment(standaloneHtml, filenameBase) {
  try {
    const logoInlined = await inlineBrandLogoForEmail(standaloneHtml);
    const pdfB64 = await receiptHtmlToPdfBase64(logoInlined, { widthMm: 80 });
    if (!pdfB64) return null;
    const safe = String(filenameBase || 'beleg').replace(/[^\w.-]+/g, '_').slice(0, 80) || 'beleg';
    return [{ filename: `${safe}.pdf`, content: pdfB64 }];
  } catch (e) {
    console.warn('[mail-pdf] PDF-Anhang-Erzeugung fehlgeschlagen, sende ohne Anhang:', e);
    return null;
  }
}

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], tables: [], settings: {} };
}

/**
 * Ersetzt die Platzhalter {name}, {date}, {time}, {guests}, {area},
 * {restaurant}, {phone}, {preorder_block}, {notes_block}, {cancel_link}.
 */
export function fillTemplate(tpl, r) {
  const state = getState();
  // Vorbestellung: kennt v1-Array UND v2-Object (mit Streichungen, Patty-Swap,
  // Extras, Soßen, Sonderwünschen). Bei v2 wird das alles unter dem Item-Namen
  // zeilenweise eingerückt — die Küche soll sofort sehen, was geändert ist.
  const preorderItems = normalizePreorderItems(r.preorder);
  const isTakeaway = (r.serviceType === 'takeaway');
  let preorderBlock = '';
  if (preorderItems.length) {
    const lines = preorderItems.map(it => {
      let row = `• ${it.qty}x ${it.displayName} (${fmtEUR(it.lineTotal)})`;
      if (it.note) row += `\n    ${it.note}`;
      return row;
    });
    const header = isTakeaway ? 'Deine Bestellung zur Abholung:' : 'Deine Vorbestellung:';
    preorderBlock = header + '\n' + lines.join('\n');
    const special = preorderSpecialRequests(r.preorder);
    if (special) preorderBlock += `\n\nSonderwünsche / Allergien: ${special}`;
  }
  const notesBlock = r.notes ? 'Dein Hinweis: ' + r.notes : '';

  // Helper, die noch in app.js leben: tableNamesFor + areaLabel
  const areaLabelFn = (typeof window.areaLabel === 'function')
    ? window.areaLabel
    : (a => a || '');
  const tableNamesFn = (typeof window.tableNamesFor === 'function')
    ? window.tableNamesFor
    : (ids => (ids || []).join(', '));

  const areaText = (r.assignedTables && r.assignedTables.length)
    ? `${areaLabelFn((state.tables.find(t => t.id === r.assignedTables[0]) || {}).area || r.area)} (Tisch ${tableNamesFn(r.assignedTables)})`
    : areaLabelFn(r.area);

  const cancelLink = buildCancelLink(r);

  // S58 Block I.4 — Defensiver Cancel-Block-Strip vor Token-Substitution.
  // Bei NULL cancelToken (z.B. manuell angelegte Reservation, oder
  // Reservation aus pre-Migration-Phase ohne Token) würde die Mail sonst
  // "Falls sich deine Pläne ändern, kannst du hier stornieren:" mit leerer
  // Folgezeile rendern — wirkt buggy. Wir stripen den ganzen Block.
  const tplPrepared = stripEmptyCancelBlock(tpl || '', !!cancelLink);

  return tplPrepared
    .replace(/{name}/g, r.name)
    .replace(/{date}/g, fmtDate(r.date))
    .replace(/{time}/g, r.time)
    .replace(/{guests}/g, r.guests)
    .replace(/{area}/g, areaText)
    .replace(/{restaurant}/g, state.settings.restaurantName || '')
    .replace(/{phone}/g, state.settings.restaurantPhone || '')
    .replace(/{preorder_block}/g, preorderBlock)
    .replace(/{notes_block}/g, notesBlock)
    .replace(/{cancel_link}/g, cancelLink)
    .replace(/\n\n\n+/g, '\n\n');
}

/**
 * S58 Block I.4 — Defensiver Cancel-Block-Strip.
 *
 * Wenn kein Storno-Link verfügbar ist (NULL Token, fehlende publicUrl, …),
 * entfernen wir den umgebenden "Falls …stornieren:"-Prompt zusammen mit der
 * Token-Zeile. Sonst rendert die Mail einen leeren Satz mit dangling Leerzeile,
 * der wie ein Bug wirkt. Pattern matcht beide Schreibweisen ({{cancel_link}}
 * und {cancel_link}) — der JS-Pfad nutzt historisch beide. Wirkt auch auf
 * tenant-customized Templates, solange sie das gleiche prompt+token-Pattern
 * beibehalten (alle 4 Default-Templates folgen dem Pattern).
 *
 * Exportiert für Tests + Wiederverwendung in fillOrderTemplate.
 */
export function stripEmptyCancelBlock(body, hasCancelLink) {
  if (typeof body !== 'string' || !body.length) return body || '';
  if (hasCancelLink) return body;
  let out = body
    .replace(/\n[^\n]*[Ss]tornier[^\n]*\n\s*\{\{\s*cancel_link\s*\}\}\s*\n?/g, '\n')
    .replace(/\n[^\n]*[Ss]tornier[^\n]*\n\s*\{\s*cancel_link\s*\}\s*\n?/g, '\n');
  out = out.replace(/\n{3,}/g, '\n\n');
  return out;
}

/**
 * Cancel-URL: zeigt zurück auf die Reservierungs-App mit `#cancel=<token>`.
 * Lookup und Storno laufen über SECURITY-DEFINER-RPCs (Session 4) — keine
 * direkte anon-RLS mehr.
 */
export function buildCancelLink(r) {
  if (!r || !r.cancelToken) return '';
  const origin = (window.location.origin && window.location.origin !== 'null')
    ? window.location.origin
    : '';
  const path = window.location.pathname.replace(/[^/]*$/, 'index.html');
  return `${origin}${path}#cancel=${r.cancelToken}`;
}

/**
 * Liefert (Nachricht, Toast-Klasse) je nach Mail-Provider:
 *   mailto → "Mailclient geöffnet"
 *   resend → "Versendet an ..."
 *   error  → Fehlertext
 */
export function mailResultToast(result, recipient) {
  if (!result) return ['E-Mail-Status unbekannt', 'error'];
  if (!result.success) {
    return ['E-Mail-Versand fehlgeschlagen: ' + (result.error || 'unbekannter Fehler'), 'error'];
  }
  if (result.provider === 'resend') {
    return ['E-Mail versendet an ' + (recipient || 'Kunde'), 'success'];
  }
  if (result.provider === 'mailto') {
    return ['E-Mail-Vorlage im Mailclient geöffnet', 'success'];
  }
  return ['E-Mail versendet', 'success'];
}


/**
 * S83 Mail-Pfad-Konsolidierung — EIN Sende-Pfad für Status-Mails.
 * ---------------------------------------------------------------------------
 * Manuelle Re-Sends (Cockpit-Buttons) liefen früher über den JS-`fillTemplate`-
 * Pfad (Reservierung single-brace `{name}`), während die Auto-Mails über
 * DB-Trigger → Outbox → Cron-Edge laufen (KIND_TEMPLATES, double-brace
 * `{{name}}`). Beide lasen denselben Override-Slot `settings.emailTemplates[kind]`
 * → es gab KEIN Token-Format, das auf beiden korrekt war (literale `{name}` in
 * der Live-Mail). Diese Funktion routet die manuellen Re-Sends jetzt ebenfalls
 * über die Outbox (RPC `resend_status_mail`, phase_81): EIN Interpolator
 * (Edge, double-brace), EINE Default-Quelle (KIND_TEMPLATES), aktuelle
 * Override-Texte zur Sendezeit. `fillTemplate`/`DEFAULT_RESERVATION_TEMPLATES`
 * bleiben als Backward-Compat-Export (window) erhalten — S94 hat die letzte
 * synchrone Received-Mail entfernt, kein JS-Auto-Sender nutzt sie mehr.
 *
 * Sicher: alle JS-Status-Auto-Listener wurden in S43b entfernt (bootstrap.js) —
 * diese Sender sind reine Re-Send-Buttons, kein Doppel-Send.
 */
export async function resendStatusMail(entityType, entityId, action) {
  const supa = window.App && window.App.supabase;
  if (!supa || typeof supa.rpc !== 'function') {
    showToast('Mail-Dienst nicht verfügbar.', 'error');
    return { ok: false, error: 'no supabase' };
  }
  const { data, error } = await supa.rpc('resend_status_mail', {
    p_entity_type: entityType, p_entity_id: entityId, p_action: action
  });
  if (error) {
    showToast('Erneuter Versand fehlgeschlagen: ' + (error.message || String(error)), 'error');
    return { ok: false, error };
  }
  if (data && data.ok) {
    showToast('E-Mail wird erneut versendet (Warteschlange).', 'success');
  } else if (data && data.reason === 'no_original') {
    showToast('Noch keine versendete Mail vorhanden — der Erstversand läuft automatisch beim Bestätigen.', 'info');
  } else {
    showToast('E-Mail konnte nicht erneut versendet werden.', 'warning');
  }
  return data || { ok: false };
}

/** Bestätigungs-Mail erneut versenden (S83: über Outbox/Edge statt JS-sync). */
export async function sendConfirmationEmail(id) {
  return resendStatusMail('reservation', id, 'confirmed');
}

/** Absage-Mail erneut versenden (S83: über Outbox/Edge). */
export async function sendDeclineEmail(id) {
  return resendStatusMail('reservation', id, 'declined');
}

// S94 — sendReservationReceivedEmail entfernt. Die Reservierungs-Eingangs-
// bestätigung wird nicht mehr versendet: der Confirmation-Screen
// (#customer-success) ist die visuelle Eingangsbestätigung, die erste echte
// Mail folgt erst nach interner Annahme (DB-Trigger enqueue_reservation_
// confirmed_mail). Tracking-Status-Stufe „Bestätigt" (customer-status.js) ist
// davon unberührt — das ist eine UI-Timeline-Stufe, kein Mail-Kind.

// S106 — sendReminderEmail entfernt. Die Erinnerungs-Mail wird nicht mehr
// manuell versendet: der Auto-Pfad (Cron enqueue_reservation_reminders →
// mail_outbox kind 'reservation_reminder_customer', idempotent via
// reminder_enqueued_at) garantiert genau 1× pro Reservierung zur eingestellten
// Vorlaufzeit (Settings reservationReminderLeadHours). Das `reminder`-Mail-
// Template (resolveReservationTemplate) bleibt — es ist die Vorlage, die der
// Edge-Versand für die automatische Erinnerung nutzt.

/* ---------------------------------------------------------------------------
 * ORDER-MAIL-PIPELINE (Phase 3, v0.21.0)
 * ---------------------------------------------------------------------------
 * Bestellungen leben in einer eigenen Datentabelle mit eigener Status-Pipeline
 * (`pending` → `confirmed` → `preparing` → `ready` → `picked_up`/`cancelled`).
 * Dazu gehören vier Mail-Wordings, die als Defaults in
 * `settings.emailTemplatesOrder` geseedet sind (Migration 34):
 *
 *   received   → nach Customer-Submit (Eingangsbestätigung)
 *   confirmed  → wenn Restaurant Bestellung angenommen hat
 *   ready      → wenn Bestellung abholbereit ist
 *   cancelled  → bei Storno (Customer- oder Restaurant-seitig)
 *
 * Templates nutzen `{{token}}`-Syntax (doppelte Curlys), abweichend von der
 * Reservation-Mail-Engine (`{token}`). Erlaubte Tokens:
 *   {{name}} {{order_number}} {{pickup_time}} {{pickup_date}}
 *   {{items}} {{total}} {{brand}} {{phone}} {{cancel_link}}
 *
 * Die Mail-Funktionen sind so geschrieben, dass sie sowohl das Minimal-
 * Objekt vom anon-Create-RPC als auch den vollen mapped Row akzeptieren —
 * Field-Lookup geht über eine Helper-Funktion, die beide Varianten kennt.
 * ------------------------------------------------------------------------- */

/** Liefert das erste vorhandene Feld aus einem Order-Objekt. */
function pickOrderField(o, ...keys) {
  if (!o) return undefined;
  for (const k of keys) {
    if (o[k] !== undefined && o[k] !== null && o[k] !== '') return o[k];
  }
  return undefined;
}

/** Order-Cancel-Link: zeigt auf die Reservierungs-App mit `#cancel=<order-token>`.
 *  Order-Tokens beginnen mit `o`, Reservation-Tokens mit `c` — der Storno-View
 *  erkennt das Präfix und dispatcht entsprechend. */
export function buildOrderCancelLink(order) {
  const token = pickOrderField(order, 'cancelToken', 'cancel_token');
  if (!token) return '';
  const origin = (window.location.origin && window.location.origin !== 'null')
    ? window.location.origin
    : '';
  const path = window.location.pathname.replace(/[^/]*$/, 'index.html');
  return `${origin}${path}#cancel=${token}`;
}

/** REF-7c Order-Tracking-Link: zeigt auf die App mit `#track=<public_status_token>`.
 *  Status-Tokens beginnen mit `s` (vs. `o`/`c` für Cancel). Leer, wenn kein
 *  Token vorhanden (z.B. Minimal-Objekt vom anon-Create vor DB-Roundtrip). */
export function buildOrderTrackingLink(order) {
  const token = pickOrderField(order, 'publicStatusToken', 'public_status_token');
  if (!token) return '';
  const origin = (window.location.origin && window.location.origin !== 'null')
    ? window.location.origin
    : '';
  const path = window.location.pathname.replace(/[^/]*$/, 'index.html');
  return `${origin}${path}#track=${token}`;
}

/** Cents → "12,50 €". */
function centsToEUR(cents) {
  return fmtEUR((Number(cents) || 0) / 100);
}

/** "HH:MM:SS" → "HH:MM"; "HH:MM" durchreichen; sonst leer. */
function trimPickupTime(t) {
  if (!t) return '';
  if (typeof t !== 'string') return String(t);
  return t.length >= 5 ? t.slice(0, 5) : t;
}

/** Multiline-Items-Block aus dem Preorder-Snapshot (v2 oder v1). Bei
 *  fehlendem Snapshot Fallback auf eine einzeilige Total-Summe. */
function buildOrderItemsBlock(order) {
  const snap = pickOrderField(order, 'preorderSnapshot', 'preorder_snapshot');
  const list = normalizePreorderItems(snap);
  if (!list.length) return '';
  return list.map(it => {
    let row = `• ${it.qty}x ${it.displayName} (${fmtEUR(it.lineTotal)})`;
    if (it.note) row += `\n    ${it.note}`;
    return row;
  }).join('\n');
}

/**
 * Ersetzt alle {{tokens}} in einer Order-Mail-Vorlage. Akzeptiert sowohl
 * das Minimal-Objekt (vom anon-Create) als auch den vollen mapped Order-Row.
 */
export function fillOrderTemplate(tpl, order) {
  if (!tpl) return '';
  const state = getState();

  // S134c (Befund 3) — Platzhalter-Namen (Theke/Tisch/Order-Nr) → leere Anrede.
  const name        = displayGuestName(pickOrderField(order, 'customerName', 'name', 'customer_name'));
  const orderNumber = pickOrderField(order, 'orderNumber', 'order_number') || '';
  const pickupTime  = trimPickupTime(pickOrderField(order, 'pickupTime', 'pickup_time'));
  const pickupDate  = pickOrderField(order, 'pickupDate', 'pickup_date');
  const totalCents  = pickOrderField(order, 'totalGrossCents', 'total_gross_cents');
  const brand       = state.settings.restaurantName || '';
  const phone       = state.settings.restaurantPhone || '';
  const cancelLink  = buildOrderCancelLink(order);
  const trackingLink = buildOrderTrackingLink(order);
  const itemsBlock  = buildOrderItemsBlock(order);
  const totalText   = totalCents != null ? centsToEUR(totalCents) : '';
  const pickupDateF = pickupDate ? fmtDate(pickupDate) : '';
  // Neue Tokens für Phase 10a.2 (Belegausgabepflicht-Mail)
  const invoiceNumber = pickOrderField(order, 'invoiceNumber', 'invoice_number') || '';
  const payMethodRaw  = pickOrderField(order, 'paymentMethod', 'payment_method') || '';
  const paymentLabelMap = { cash: 'Bar', card: 'EC / Kreditkarte', stripe: 'Online (Stripe)' };
  const paymentMethod = paymentLabelMap[payMethodRaw] || payMethodRaw || '—';

  // \\n in DB-Strings ist als Literal-\n geseedet → in echte Zeilenumbrüche
  // wandeln, damit die Mail wie erwartet umbricht.
  let normalized = String(tpl).replace(/\\n/g, '\n');
  // S58 Block I.4: Pre-Substitution-Strip — wenn der Token leer ist, prompt
  // mit-entfernen statt nackten Satz stehen zu lassen.
  normalized = stripEmptyCancelBlock(normalized, !!cancelLink);

  return normalized
    .replace(/\{\{name\}\}/g, name)
    .replace(/\{\{order_number\}\}/g, orderNumber)
    .replace(/\{\{pickup_time\}\}/g, pickupTime)
    .replace(/\{\{pickup_date\}\}/g, pickupDateF)
    .replace(/\{\{items\}\}/g, itemsBlock)
    .replace(/\{\{total\}\}/g, totalText)
    .replace(/\{\{brand\}\}/g, brand)
    .replace(/\{\{phone\}\}/g, phone)
    .replace(/\{\{cancel_link\}\}/g, cancelLink)
    .replace(/\{\{tracking_link\}\}/g, trackingLink)
    .replace(/\{\{invoice_number\}\}/g, invoiceNumber)
    .replace(/\{\{payment_method\}\}/g, paymentMethod)
    // S134c (Befund 3) — leere Anrede glätten: „Hallo ," → „Hallo,".
    .replace(/Hallo\s+,/g, 'Hallo,')
    .replace(/\n\n\n+/g, '\n\n');
}

/**
 * Zentrale Versand-Funktion für Order-Mails. `kind` muss einer der vier
 * Template-Keys sein (received / confirmed / ready / cancelled).
 * Akzeptiert Order-Objekt ODER Order-ID (lädt dann aus dem Cache).
 */
export async function sendOrderEmail(orderOrId, kind) {
  // S94: `received` entfernt — Eingangsbestätigung wird nicht mehr versendet.
  const allowed = ['confirmed', 'ready', 'cancelled', 'receipt'];
  if (!allowed.includes(kind)) {
    console.warn('[order-mail] unbekannter Template-Key:', kind);
    return { success: false, error: 'invalid template key' };
  }

  // S83 Mail-Pfad-Konsolidierung: Status-Mails (confirmed/ready/cancelled) als
  // manueller Re-Send laufen über die Outbox/Edge (resend_status_mail) — EIN
  // Sende-Pfad, dieselbe Engine wie die Auto-Mails (double-brace, KIND_TEMPLATES,
  // aktuelle Override-Texte). `receipt` (Belegausgabe, eigener Pfad) bleibt
  // JS-seitig.
  if (kind === 'confirmed' || kind === 'ready' || kind === 'cancelled') {
    const orderId = (typeof orderOrId === 'string')
      ? orderOrId
      : pickOrderField(orderOrId, 'id');
    if (!orderId) return { success: false, error: 'no order id' };
    const res = await resendStatusMail('order', orderId, kind);
    return { success: !!(res && res.ok), ...res };
  }

  // Order-Objekt auflösen
  let order = orderOrId;
  if (typeof orderOrId === 'string') {
    order = await window.App.storage.orders.get(orderOrId);
    if (!order) {
      console.warn('[order-mail] Order nicht gefunden:', orderOrId);
      return { success: false, error: 'order not found' };
    }
  }

  const email = pickOrderField(order, 'customerEmail', 'email', 'customer_email');
  if (!email) {
    // Bestellung ohne Mail-Adresse — nichts zu tun. Kein Fehler, sondern Skip.
    return { success: false, skipped: true, reason: 'no email' };
  }

  const state = getState();
  // S45 Block C: Tenant-Override mit DEFAULT-Fallback statt Hard-Error.
  // Verhindert, dass eine fehlende Settings-Spalte den ganzen Mail-Versand
  // killt — der DEFAULT_ORDER_TEMPLATES-Wortlaut ist immer da.
  const tpl = resolveOrderTemplate(state, kind);
  if (!tpl) {
    console.warn('[order-mail] unbekannter Template-Key:', kind);
    return { success: false, error: 'unknown template kind' };
  }
  const subject = fillOrderTemplate(tpl.subject, order);
  const body    = fillOrderTemplate(tpl.body,    order);
  const result  = await _mailWithBrandHtml({ to: email, subject, body, type: 'order_' + kind });

  if (result && result.success) {
    // Audit-Event mitnehmen (sieht das Restaurant in der History)
    if (window.App.events) {
      await window.App.events.emit('order:mail-sent', {
        orderId: pickOrderField(order, 'id'),
        kind, to: email
      });
    }
  } else {
    console.warn('[order-mail] Versand fehlgeschlagen:', kind, result && result.error);
  }
  return result;
}

/** Convenience-Wrapper, damit Aufruf-Sites lesbar bleiben.
 *  S94: sendOrderReceivedEmail entfernt (received wird nicht mehr versendet). */
export async function sendOrderConfirmedEmail(orderOrId) { return sendOrderEmail(orderOrId, 'confirmed'); }
export async function sendOrderReadyEmail(orderOrId)     { return sendOrderEmail(orderOrId, 'ready'); }
export async function sendOrderCancelledEmail(orderOrId) { return sendOrderEmail(orderOrId, 'cancelled'); }

/**
 * Phase 10a.2 (Session 15): Kassenbon per Mail an den Kunden.
 * Wird nach «Bezahlt»-Klick oder nach Stornobeleg-Ausstellung über das
 * Beleg-Aktion-Modal aufgerufen. Bei Korrekturbelegen wird das Subject
 * automatisch um den «Stornobeleg»-Hinweis ergänzt.
 */
export async function sendOrderReceiptEmail(orderOrId) {
  // Order auflösen, damit wir is_correction prüfen können
  let order = orderOrId;
  if (typeof orderOrId === 'string') {
    order = await window.App.storage.orders.get(orderOrId);
    if (!order) {
      console.warn('[order-mail] Order nicht gefunden:', orderOrId);
      return { success: false, error: 'order not found' };
    }
  }
  const email = pickOrderField(order, 'customerEmail', 'email', 'customer_email');
  if (!email) {
    return { success: false, skipped: true, reason: 'no email' };
  }
  const isCorrection = !!pickOrderField(order, 'isCorrection', 'is_correction');
  const orderId = pickOrderField(order, 'id');
  const kind = isCorrection ? 'order_receipt_correction' : 'order_receipt';

  // S225 CUT B — Kind-Toggle-Spiegel (Belege). Deaktiviert → kein Enqueue, aber
  // aktiver Feedback-Toast (Operator klickt hier bewusst, kein Silent-Skip).
  if (!isMailKindEnabled(getState().settings, kind)) {
    showToast('Diese Beleg-Mail ist deaktiviert (Einstellungen → Vorlagen → Belege).', 'info');
    return { success: false, skipped: true, reason: 'kind_disabled' };
  }

  // S213-C1 — Ein-Pfad-Architektur: die PDF-ERZEUGUNG bleibt client-seitig (S213-A,
  // im Moment der Bon-Entstehung). Hier nur (1) pdf_path-Garantie (Alt-Bon lazy
  // nachziehen) und (2) server-seitiger VERSAND via enqueue → process-mail-outbox
  // hängt das GESPEICHERTE PDF an. Kein Direktversand / on-the-fly-Anhang mehr.
  // Versand wird dadurch asynchron (~60 s Cron) → Aufrufer toastet „Warteschlange".
  await _ensureOrderReceiptPdf(orderId, order);
  const result = await _enqueueOrderReceiptRpc(orderId, kind, email);

  if (result && result.success && window.App.events) {
    await window.App.events.emit('order:mail-sent', {
      orderId,
      kind: isCorrection ? 'receipt_correction' : 'receipt',
      to: email
    });
  }
  return result;
}

/**
 * S213-C1 — pdf_path-Garantie vor dem Enqueue. Ist der Bon bereits mit PDF
 * archiviert (Regelfall: beim Kassieren, S213-A), passiert nichts. Fehlt es
 * (Alt-Bon vor phase_159), wird es client-seitig aus dem Standalone-HTML erzeugt,
 * in den Bucket geladen und `pdf_path` gesetzt (UPDATE nur owner/admin — RLS;
 * best-effort). Best-effort insgesamt: scheitert es, sendet die Edge defensiv
 * ohne Anhang (Cover-Text) + Warn-Log.
 */
async function _ensureOrderReceiptPdf(orderId, order) {
  try {
    const ba = window.App && window.App.storage && window.App.storage.bonArchive;
    if (!ba || !orderId) return null;
    const cache = (typeof ba.getCache === 'function') ? ba.getCache() : {};
    const entry = cache && cache[orderId];
    if (entry && entry.pdfPath) return entry.pdfPath;   // schon da → nichts tun
    const { buildStandaloneReceiptHtml } = await import('../views/admin/orders-receipt-actions.js');
    const standalone = await buildStandaloneReceiptHtml(order);
    const belegId = pickOrderField(order, 'invoiceNumber', 'invoice_number')
      || pickOrderField(order, 'orderNumber', 'order_number')
      || orderId || 'beleg';
    const att = await _buildReceiptPdfAttachment(standalone, `Beleg-${belegId}`);
    const base64 = att && att[0] && att[0].content;
    if (!base64) return null;
    const path = await ba.uploadPdfBase64(orderId, base64);
    try { await ba.setPdfPath(orderId, path); }
    catch (e) { console.warn('[order-mail] pdf_path-Backfill übersprungen (RLS/owner-only):', e); }
    return path;
  } catch (e) {
    console.warn('[order-mail] pdf_path-Garantie fehlgeschlagen (Edge sendet ohne Anhang):', e);
    return null;
  }
}

/** S213-C1 — Order-Beleg in die Warteschlange stellen (SECURITY-DEFINER-RPC,
 *  tenant-scoped). Der Cron/Edge sendet dann mit gespeichertem PDF-Anhang. */
async function _enqueueOrderReceiptRpc(orderId, kind, email) {
  const supa = window.App && window.App.supabase;
  if (!supa || typeof supa.rpc !== 'function') {
    return { success: false, error: 'Supabase nicht verfügbar.' };
  }
  const { data, error } = await supa.rpc('enqueue_order_receipt_mail', {
    p_order_id: orderId, p_kind: kind, p_recipient_email: email
  });
  if (error) {
    console.error('[order-mail] enqueue_order_receipt_mail:', error);
    return { success: false, error: error.message || String(error) };
  }
  return { success: true, queued: true, outboxId: data };
}

/**
 * Phase 29 (S27): Z-Bon per Mail an den Steuerberater (oder fallback an die
 * hinterlegte Restaurant-Mail). Empfänger-Reihenfolge:
 *   1. settings.accountantEmail   (neu, S27)
 *   2. settings.restaurantEmail   (Fallback — wie auf dem Bon-Header)
 * Wenn keiner gesetzt ist, gibt die Funktion {success:false, skipped:true}
 * zurück und das Modal toastet einen Hint.
 *
 * Payload: vorgerendertes Standalone-HTML aus day-close.js (mit Brand-CSS
 * + Z-Bon-Receipt). Aktuell als HTML-Body — Attachment-Pfad ist im
 * App.mailer noch nicht überall vorhanden. Subject enthält Z-Bon-Nr +
 * Datum für die Buchhaltung.
 */
export async function sendZBonReceiptEmail(payload) {
  if (!payload || !payload.dateIso) {
    return { success: false, error: 'no payload' };
  }
  const state = getState();
  const s = state.settings || {};
  const to = (s.accountantEmail || s.restaurantEmail || '').trim();
  if (!to) {
    return { success: false, skipped: true, reason: 'no recipient' };
  }

  // S225 CUT B — Kind-Toggle-Spiegel (Z-Bon-Beleg). Deaktiviert → kein Enqueue.
  if (!isMailKindEnabled(s, 'zbon_receipt')) {
    showToast('Die Z-Bon-Mail ist deaktiviert (Einstellungen → Vorlagen → Exporte).', 'info');
    return { success: false, skipped: true, reason: 'kind_disabled' };
  }

  // S213-C2 — Ein-Pfad-Architektur: die Z-Bon-PDF-Erzeugung + -Ablage passiert
  // client-seitig beim Tagesabschluss (day-close → zbon_archive.pdf_path). Hier
  // NUR der server-seitige Versand via enqueue → process-mail-outbox hängt das
  // GESPEICHERTE PDF an (Lookup über zbon_archive_id im Payload). Kein
  // Direktversand / on-the-fly-PDF mehr. Async (~60 s Cron) → „Warteschlange".
  const supa = window.App && window.App.supabase;
  if (!supa || typeof supa.rpc !== 'function') {
    return { success: false, error: 'Supabase nicht verfügbar.' };
  }
  const { data, error } = await supa.rpc('enqueue_zbon_receipt_mail', {
    p_zbon_date: payload.dateIso, p_recipient_email: to
  });
  if (error) {
    console.error('[zbon-mail] enqueue_zbon_receipt_mail:', error);
    return { success: false, error: error.message || String(error) };
  }
  if (window.App.events) {
    await window.App.events.emit('zbon:mail-sent', {
      zBonNumber: payload.zBonNumber || '', date: payload.dateIso, to
    });
  }
  return { success: true, queued: true, to, outboxId: data };
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    fillTemplate,
    buildCancelLink,
    mailResultToast,
    sendConfirmationEmail,
    sendDeclineEmail,
    fillOrderTemplate,
    buildOrderCancelLink,
    sendOrderEmail,
    sendOrderConfirmedEmail,
    sendOrderReadyEmail,
    sendOrderCancelledEmail,
    sendOrderReceiptEmail,
    sendZBonReceiptEmail
  });
}
